import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import React, { lazy, Suspense } from "react";
import {
  ChakraProvider,
  ColorModeScript,
  IconButton,
  useColorMode,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { FaSun, FaMoon } from "react-icons/fa";
import theme from "./theme";
// Bootstrap is now imported via index.js with custom build
import "./index.css";

// Critical components - loaded immediately (above the fold)
import TheNavbar from "./components/Navbar.jsx";
import Home from "./pages/Home.jsx";
import ScrollToTop from "./js/ScrollToTop.js";
import Footer from "./components/Footer.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import PageLoader from "./components/PageLoader.jsx";
import BookExamPage from "./pages/BookExamPage.jsx";

// Lazy load all non-critical routes for better initial load
const AdminMessageForm = lazy(() => import("./components/AdminMessageForm.jsx"));
const UserMessages = lazy(() => import("./pages/Announcements.jsx"));
const AdminDashboard = lazy(() => import("./pages/admin.jsx"));
const Appy = lazy(() => import("./appy.jsx"));
const UserManagement = lazy(() => import("./pages/UserManagement.jsx"));
const NotFound = lazy(() => import("./pages/404.js"));
const Register = lazy(() => import("./pages/register.jsx"));
const AboutUs = lazy(() => import("./pages/AboutUs.jsx"));
const StudentRegisterForm = lazy(() => import("./pages/StudentRegisterForm.jsx"));
const HRExam = lazy(() => import("./pages/HRExam.jsx"));
const StudentDetails = lazy(() => import("./pages/studentPage.jsx"));
const InvoiceForm = lazy(() => import("./components/create-invoice.jsx"));
const AllInvoices = lazy(() => import("./pages/AllInvoices.jsx"));
const ConfirmedStudents = lazy(() => import("./pages/ConfirmedStage.jsx"));
const NotConfirmedStudents = lazy(() => import("./pages/NotConfirmedStage.jsx"));
const InvoiceStudents = lazy(() => import("./pages/InvoiceStage.jsx"));
const InvoiceConfirmedStudents = lazy(() => import("./pages/InvoiceConfirmedStage.jsx"));
const ExamDetailsStage = lazy(() => import("./pages/ExamDetailsStage.jsx"));
const ResultStage = lazy(() => import("./pages/ResultStage.jsx"));
const HRSection = lazy(() => import("./pages/Hr.jsx"));
const PMP = lazy(() => import("./pages/PMP.jsx"));
const SupplyChain = lazy(() => import("./pages/SupplyChain.jsx"));
const DoneStage = lazy(() => import("./pages/DoneStage.jsx"));
const SaudiArabiaExamsContainer = lazy(() => import("./pages/SaudiArabiaExams.jsx"));
const UAEExamsContainer = lazy(() => import("./pages/UAEExams.jsx"));
const DubaiEMOHExamsContainer = lazy(() => import("./pages/DubaiEMOHExams.jsx"));
const QatarExamsContainer = lazy(() => import("./pages/QatarExams.jsx"));
const KuwaitExamsContainer = lazy(() => import("./pages/KuwaitExams.jsx"));
const BahrainExamsContainer = lazy(() => import("./pages/BahrainExams.jsx"));
const ContactForm = lazy(() => import("./components/Contact-us.jsx"));
const ContactUsPage = lazy(() => import("./pages/ContactUsPage.jsx"));
const InquiriesPage = lazy(() => import("./pages/InquiriesPage.jsx"));
const RegisterTestCenter = lazy(() => import("./pages/RegisterTestCenter.jsx"));
const TestCenterDashboard = lazy(() => import("./pages/TestCenterDashboard.jsx"));
const TestCenters = lazy(() => import("./pages/ViewTestCenter.jsx"));
const ExamScheduleManagement = lazy(() => import("./pages/ExamScheduleManagement.jsx"));
const PaymentManagement = lazy(() => import("./pages/PaymentManagement.jsx"));
const SalesDashboard = lazy(() => import("./pages/SalesDashboard.jsx"));
const CreateSalesInquiry = lazy(() => import("./pages/CreateSalesInquiry.jsx"));
const EditSalesInquiry = lazy(() => import("./pages/EditSalesInquiry.jsx"));
const FueDashboard = lazy(() => import("./pages/FueDashboard.jsx"));
const CreateFueInquiry = lazy(() => import("./pages/CreateFueInquiry.jsx"));
const EditFueInquiry = lazy(() => import("./pages/EditFueInquiry.jsx"));
const DiscountCalculator = lazy(() => import("./pages/DiscountCalculator.jsx"));
const EventInvitation = lazy(() => import("./pages/EventInvitation.jsx"));
const EventInvitesDashboard = lazy(() => import("./pages/EventInvitesDashboard.jsx"));
const BulkStudentUpload = lazy(() => import("./pages/BulkStudentUpload.jsx"));
const VoucherManagement = lazy(() => import("./pages/VoucherManagement.jsx"));
const ManageExternalUsers = lazy(() => import("./pages/ManageExternalUsers.jsx"));
const ExternalStudentRegisterForm = lazy(() => import("./pages/ExternalStudentRegisterForm.jsx"));
const ExternalExamDateManagement = lazy(() => import("./pages/ExternalExamDateManagement.jsx"));
const ExternalStudentDetails = lazy(() => import("./pages/ExternalStudentDetails.jsx"));
const AgencyRankings = lazy(() => import("./pages/AgencyRankings.jsx"));
const BecomeTestCenter = lazy(() => import("./pages/BecomeTestCenter.jsx"));
const EnglishForAdultsPage = lazy(() => import("./pages/EnglishForAdultsPage.jsx"));
const TOEFLPage = lazy(() => import("./pages/TOEFLPage.jsx"));
const IELTSPage = lazy(() => import("./pages/IELTSPage.jsx"));
const MicrosoftProgramsPage = lazy(() => import("./pages/MicrosoftProgramsPage.jsx"));
const EnglishForKidsPage = lazy(() => import("./pages/EnglishForKidsPage.jsx"));
const TEFLPage = lazy(() => import("./pages/TEFLPage.jsx"));
const HRCoursePage = lazy(() => import("./pages/HRCoursePage.jsx"));
const ArabicForForeignersPage = lazy(() => import("./pages/ArabicForForeignersPage.jsx"));
const AllInstallments = lazy(() => import("./pages/AllInstallments.jsx"));
const CreateInstallment = lazy(() => import("./pages/CreateInstallment.jsx"));

// PageLoader is now imported from components/PageLoader.jsx

function AppContent() {
  const { colorMode, toggleColorMode } = useColorMode();
  const location = useLocation();

  // Define routes where navbar and footer should be hidden
  const hideLayoutRoutes = ['/Contact-us', '/event-invitation'];
  const shouldHideLayout = hideLayoutRoutes.includes(location.pathname);
  const shouldHideFooter = hideLayoutRoutes.includes(location.pathname);

  // Protect /inquiries from auditors: small wrapper component
  const InquiryRoute = () => {
    const token = localStorage.getItem('token');
    if (!token) return <Navigate to="/login" replace />;
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        if ( payload.role === 'sales') {
          return <Navigate to="/404" replace />;
        }
      }
    } catch (e) {
      console.error('Error parsing token for inquiries route:', e);
      return <Navigate to="/login" replace />;
    }
    return <InquiriesPage />;
  };

  // Protect /event-invites-dashboard from reviewers
  const EventInvitesDashboardRoute = () => {
    const token = localStorage.getItem('token');
    if (!token) return <Navigate to="/login" replace />;
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        if (payload.role === 'reviewer') {
          return <Navigate to="/404" replace />;
        }
      }
    } catch (e) {
      console.error('Error parsing token for event-invites-dashboard route:', e);
      return <Navigate to="/login" replace />;
    }
    return (
      <Suspense fallback={<PageLoader />}>
        <ProtectedRoute requireAdmin={true}>
          <EventInvitesDashboard />
        </ProtectedRoute>
      </Suspense>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ScrollToTop />
      {!shouldHideLayout && <TheNavbar />}
      <Suspense fallback={<PageLoader />}>
        <main style={{ flex: '1 0 auto' }}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/book-exam/:examCategory" element={<BookExamPage />} />
            <Route path="/external-register" element={<ExternalStudentRegisterForm />} />
            <Route path="/agency-rankings" element={<AgencyRankings />} />
            <Route path="/Contact-us" element={<ContactUsPage />} />
            <Route path="/hr-exam" element={<HRExam />} />
            <Route path="/become-test-center" element={<BecomeTestCenter />} />
            <Route path="/courses/english-for-adults" element={<EnglishForAdultsPage />} />
            <Route path="/courses/toefl" element={<TOEFLPage />} />
            <Route path="/courses/ielts" element={<IELTSPage />} />
            <Route path="/courses/microsoft-programs" element={<MicrosoftProgramsPage />} />
            <Route path="/courses/english-for-kids" element={<EnglishForKidsPage />} />
            <Route path="/courses/tefl" element={<TEFLPage />} />
            <Route path="/courses/hr-course" element={<HRCoursePage />} />
            <Route path="/courses/arabic-for-foreigners" element={<ArabicForForeignersPage />} />
            
            <Route path="/contact-form-old" element={<ContactForm />} />
            <Route path="/inquiries" element={<InquiryRoute />} />
            <Route path="/Programs/hr" element={<HRSection />} />
            <Route path="/PMP" element={<PMP />} />
            {/* <Route path="/FAQ" element={<FAQPage />} /> */}
            <Route path="/SupplyChain" element={<SupplyChain />} />
            <Route path="/registeruser" element={<Register />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/manage-external-users" element={
              <ProtectedRoute requireAdmin={true}>
                <ManageExternalUsers />
              </ProtectedRoute>
            } />
            <Route path="/CreateAnnouncement" element={<AdminMessageForm />} />
            <Route path="/Announcements" element={<UserMessages />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/exam-schedule" element={
              <ProtectedRoute requireAdmin={true}>
                <ExamScheduleManagement />
              </ProtectedRoute>
            } />
            <Route path="/external-exam-dates" element={
              <ProtectedRoute requireAdmin={true}>
                <ExternalExamDateManagement />
              </ProtectedRoute>
            } />
            <Route path="/payment-management" element={
              <ProtectedRoute requireAdmin={true}>
                <PaymentManagement />
              </ProtectedRoute>
            } />
            <Route path="/discount-calculator" element={
              <ProtectedRoute requireAdmin={true}>
                <DiscountCalculator />
              </ProtectedRoute>
            } />
            <Route path="/sales-dashboard" element={
              <ProtectedRoute requireAdmin={true}>
                <SalesDashboard />
              </ProtectedRoute>
            } />
            <Route path="/create-sales-inquiry" element={
              <ProtectedRoute requireAdmin={true} requireEditor={true}>
                <CreateSalesInquiry />
              </ProtectedRoute>
            } />
            <Route path="/edit-sales-inquiry/:id" element={
              <ProtectedRoute requireAdmin={true} requireEditor={true}>
                <EditSalesInquiry />
              </ProtectedRoute>
            } />
            <Route path="/fue-dashboard" element={
              <ProtectedRoute requireAdmin={true}>
                <FueDashboard />
              </ProtectedRoute>
            } />
            <Route path="/create-fue-inquiry" element={
              <ProtectedRoute requireAdmin={true} requireEditor={true}>
                <CreateFueInquiry />
              </ProtectedRoute>
            } />
            <Route path="/edit-fue-inquiry/:id" element={
              <ProtectedRoute requireAdmin={true} requireEditor={true}>
                <EditFueInquiry />
              </ProtectedRoute>
            } />
            <Route path="/students/:id" element={
              <ProtectedRoute>
                <StudentDetails />
              </ProtectedRoute>
            } />
            <Route path="/external-students/:id" element={
              <ProtectedRoute requireAdmin={true}>
                <ExternalStudentDetails />
              </ProtectedRoute>
            } />
            <Route path="/Register" element={
              <ProtectedRoute>
                <StudentRegisterForm />
              </ProtectedRoute>
            } />
            <Route path="/create-invoice/:studentId" element={<InvoiceForm />} />
            <Route path="/View/invoices" element={<AllInvoices />} />
            {/* <Route path="/Medical-exams" element={<ExamCardsContainer />} /> */}
            <Route path="/Prometric-Exams-for-the-Saudi-commission-for-Health-Specialties-SCFHS" element={<SaudiArabiaExamsContainer />} />
            <Route path="/Prometric-Exams-for-the-Dubai-Health-Authority-DHA" element={<UAEExamsContainer />} />
            <Route path="/Prometric-Exams-for-the-Emirates-Ministry-of-Health-Dubai-EMOH" element={<DubaiEMOHExamsContainer />} />
            <Route path="/Prometric-Exams-for-the-Department-of-Healthcare-Professions-DHP" element={<QatarExamsContainer />} />
            <Route path="/Prometric-Exams-for-the-Kuwait-Ministry-of-Health-MOHKW" element={<KuwaitExamsContainer />} />
            <Route path="/Prometric-Exams-for-the-National-Health-Regulatory-Authority-NHRA" element={<BahrainExamsContainer />} />
            <Route path="/Stage/confirmed" element={<ConfirmedStudents />} />
            <Route
              path="/Stage/Not-confirmed"
              element={<NotConfirmedStudents />}
            />
            <Route path="/Stage/Invoice" element={<InvoiceStudents />} />
            <Route
              path="/Stage/Invoice_Confirmed"
              element={<InvoiceConfirmedStudents />}
            />
            <Route path="/Stage/Exam_Details" element={<ExamDetailsStage />} />
            <Route path="/Stage/Results" element={<ResultStage />} />
            <Route path="/Stage/Done" element={<DoneStage />} />
            <Route path="/Register-test-center" element={<RegisterTestCenter />} />
            <Route path="/test-center/:testCenterId" element={<TestCenterDashboard />} />
            <Route path="/View-test-center" element={<TestCenters />} />
            <Route path="/event-invitation" element={<EventInvitation />} />
            <Route path="/event-invites-dashboard" element={<EventInvitesDashboardRoute />} />
            <Route path="/bulk-student-upload" element={
              <ProtectedRoute requireAdmin={true}>
                <BulkStudentUpload />
              </ProtectedRoute>
            } />
            <Route path="/voucher-management" element={
              <ProtectedRoute requireAdmin={true}>
                <VoucherManagement />
              </ProtectedRoute>
            } />
            <Route path="/installments" element={
              <ProtectedRoute requireAdmin={true}>
                <AllInstallments />
              </ProtectedRoute>
            } />
            <Route path="/create-installment" element={
              <ProtectedRoute requireAdmin={true}>
                <CreateInstallment />
              </ProtectedRoute>
            } />
            <Route path="/create-installment/:studentId" element={
              <ProtectedRoute requireAdmin={true}>
                <CreateInstallment />
              </ProtectedRoute>
            } />
            <Route path="/login" element={<Appy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        {!shouldHideFooter && <Footer />}
      </Suspense>
      <IconButton
        icon={colorMode === "light" ? <FaMoon /> : <FaSun />}
        isRound
        size="lg"
        onClick={toggleColorMode}
        position="fixed"
        bottom="4"
        right="4"
        zIndex="overlay"
        aria-label={colorMode === "light" ? "Switch to dark mode" : "Switch to light mode"}
        bg={colorMode === "light" ? "gray.800" : "gray.200"}
        color={colorMode === "light" ? "white" : "gray.800"}
        _hover={{
          bg: colorMode === "light" ? "gray.700" : "gray.300",
          transform: "scale(1.05)"
        }}
        boxShadow="lg"
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
